import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { NowSlideIn } from '@now/dpl';
import { Offcanvas } from 'bootstrap';
import { combineLatest, distinctUntilChanged, filter, map, ReplaySubject, takeUntil } from 'rxjs';
import { AngebotTermineKachelComponent } from '../angebot-termine-kachel/angebot-termine-kachel.component';
import { AngebotTermineSliderComponent } from './angebot-termine-slider/angebot-termine-slider.component';
import { getLabelFromWeiterbildungsart } from '../../model/weiterbildungsarten';
import { ScreenSizeService, ViewMode } from '../../services/screen-size';
import { CleanupService } from '../../shared/utils/cleanup.service';
import { CommonStateService } from '../../services/state/common-state.service';
import { AccumulatedBildungsangeboteService } from '../../services/accumulatedBildungsangebote.service';

@Component({
  selector: 'now-wbsuche-lib-angebot-termine',
  templateUrl: './angebot-termine.component.html',
  styleUrls: ['./angebot-termine.component.scss'],
  imports: [CommonModule, AngebotTermineKachelComponent, AngebotTermineSliderComponent]
})
export class AngebotTermineComponent implements OnDestroy, AfterViewInit {
  private readonly screenSizeService = inject(ScreenSizeService);
  private readonly cleanupService = inject(CleanupService);
  private readonly commonStateService = inject(CommonStateService);
  private readonly accumulatedBildungsangeboteService = inject(AccumulatedBildungsangeboteService);

  private _slidein!: ElementRef;

  @ViewChild('slidein')
  public set slidein(slidein: ElementRef) {
    this.slideinChanged(slidein);
  }

  @Output()
  terminSelected = new EventEmitter<string>();

  public selectedAngebot$ = this.accumulatedBildungsangeboteService.selectedAngebot$;
  public rangeFromLargeDesktop$ = this.screenSizeService.rangeFrom$(ViewMode.LARGE_DESKTOP);
  private readonly selectedAngebotId$ = this.selectedAngebot$.pipe(map((angebot) => angebot?.id));
  private readonly slideinRendered$ = new ReplaySubject<boolean>(0);
  private dplSlideIn?: NowSlideIn;

  ngAfterViewInit(): void {
    this.initializeSlideinOnMobile();
  }

  private initializeSlideinOnMobile(): void {
    combineLatest([
      this.rangeFromLargeDesktop$.pipe(distinctUntilChanged()),
      this.selectedAngebotId$,
      this.slideinRendered$
    ])
      .pipe(
        takeUntil(this.cleanupService.destroyed$),
        filter(([isDesktopOrBigger, selectedAngebotId, slideinRendered]) => {
          return !isDesktopOrBigger && !!selectedAngebotId && slideinRendered;
        }),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.showAngebotSlidein();
      });
    this.rangeFromLargeDesktop$
      .pipe(
        takeUntil(this.cleanupService.destroyed$),
        filter((isDesktopOrBigger) => isDesktopOrBigger)
      )
      .subscribe(() => {
        this.closeSlidein();
      });
    //Der folgende Code sorgt dafür, dass das Slide-in geschlossen wird, wenn es kein angebot gibt.
    //Wenn das Slide-in geöffnet war, konnte man vorher mit browser back in einen Zustand,
    // wo das Slide-in nicht ordentlich geschlossen war und man daher das scrollen gesperrt war.
    this.selectedAngebot$
      .pipe(
        filter(
          (angebot) => !angebot // null or undefined
        )
      )
      .subscribe(() => this.closeSlidein());
  }

  public weiterbildungsangebot$ = this.selectedAngebot$.pipe(
    filter(
      (angebot) => !!angebot // null or undefined
    ),
    map((angebot) => getLabelFromWeiterbildungsart(angebot?.weiterbildungsart))
  );

  ngOnDestroy(): void {
    this.closeSlidein();
    // Vorerst Workaround um NOWSUCHE-2241 zubeheben. Problem wird aber noch in der DPL behandelt.
    this._slidein?.nativeElement.addEventListener('hidden.bs.offcanvas', () => {
      this.dplSlideIn?.dispose();
    });
  }

  private slideinChanged(slidein: ElementRef) {
    this._slidein = slidein;
    this.slideinRendered$.next(!!slidein);
  }

  private closeSlidein(): void {
    this.dplSlideIn?.hide();
  }

  public slideinCloseClicked(): void {
    this.closeSlidein();
    this.commonStateService.setSelectedAngebotId(null);
  }

  private showAngebotSlidein(): void {
    if (this._slidein) {
      this.dplSlideIn = NowSlideIn.init(this._slidein.nativeElement, {
        backdrop: false
      } as Offcanvas.Options);

      this.dplSlideIn?.show();
    }
  }
}
